import Button from "core/buttons/button/Button";
import _ from "lodash";
import { ACTIONS, ENTITIES, VAR_TYPES } from "constants/constants";
import { HandleModaleBO } from "components/bo/HandleModaleBO";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { TableManager } from "components/bo/admin-table/TableManager";
import { navigateView, navigateEdit, navigateToParent } from "utils/boHelper";
import { useCountryContext } from "api/settings/country/CountryProvider";
import { useCountryCoefxContext } from "api/settings/country-coefx/CountryCoefxProvider";
import { useCurrencyContext } from "api/settings/currency/CurrencyProvider";
import { useEffect, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useUserContext } from "api/user/UserProvider";

export default function AdminCountries({ action = null, actionCoefx = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const { id, coefxID } = useParams();
  const {
    countryByIdGetter,
    countryCreate,
    countryDeleteById,
    countryList,
    countryListGetter,
    countryPerId,
    countryUpdateById,
  } = useCountryContext();
  const {
    countryCoefxCreate,
    countryCoefxDeleteById,
    countryCoefxList,
    countryCoefxListGetter,
    countryCoefxUpdateById,
  } = useCountryCoefxContext();
  const { userScope } = useUserContext();

  const { currencyListGetter, currencyList } = useCurrencyContext();
  const [deleteData, setDeleteData] = useState(null);
  const [deleteCoef, setDeleteCoef] = useState(null);
  const [defaultCurrencyList, setDefaultCurrencyList] = useState([]);
  useEffect(() => {
    currencyListGetter();
    countryListGetter();
  }, [countryListGetter, currencyListGetter]);

  useEffect(() => {
    if (id) {
      countryByIdGetter(id);
      countryCoefxListGetter(id);
    }
  }, [id, coefxID]);
  useEffect(() => {
    if (Number(id) === countryPerId.id) {
      setDefaultCurrencyList(
        currencyList
          .filter((elm) => countryPerId?.currencies?.includes(elm.id))
          ?.map((elm) => ({
            value: elm.id,
            name: elm.currency_name,
          }))
      );
    } else {
      setDefaultCurrencyList([]);
    }
  }, [countryPerId, id, location]);

  const inputs = () => [
    { col: "name", mandatory: true, isMissingValue: null, editable: true },
    {
      col: "default_language",
      mandatory: true,
      editable: true,
      isMissingValue: null,
      type: VAR_TYPES.DROPDOWN,
      choices: [
        {
          name: "FR",
          value: "FR",
        },
        {
          name: "EN",
          value: "EN",
        },
      ],
    },
    {
      col: "mission_coefficient",
      mandatory: true,
      type: VAR_TYPES.NUMBER,
      step: "0.01",
      isMissingValue: null,
      editable: true,
    },
    {
      col: "currencies",
      label: intl.formatMessage({
        id: "admin_country_page_table_name_currencies",
        defaultMessage: "Devises",
      }),
      placeholder: intl.formatMessage({
        id: "admin_country_page_table_name_currencies",
        defaultMessage: "Rechercher",
      }),
      isMissingValue: null,
      editable: true,
      popup: {
        title: {
          id: "admin_country_page_table_name_currencies_search",
          defaultMessage: "Rechercher et sélectionner une devise",
        },
      },
      mandatory: true,
      type: VAR_TYPES.TAGS,
      choices: currencyList.map((elm) => ({
        label: elm.currency_name,
        value: elm.id,
      })),
    },
    {
      col: "default_cur",
      mandatory: true,
      type: VAR_TYPES.DROPDOWN,
      toBeParsed: true,
      isMissingValue: null,
      editable: true,
      choices:
        defaultCurrencyList.length === 0
          ? [{ value: null, name: "" }]
          : defaultCurrencyList,
    },
  ];
  const coeffInputs = () => [
    {
      col: "bottom_threshold",
      mandatory:
        countryCoefxList?.length === 0 ||
        countryCoefxList[0]?.id === Number(coefxID)
          ? false
          : true,
      isMissingValue: null,
      type: VAR_TYPES.NUMBER,
      step: "0.01",
      editable: true,
      disabled:
        countryCoefxList?.length === 0 ||
        countryCoefxList[0]?.id === Number(coefxID),
      defaultValue:
        countryCoefxList?.length === 0 ||
        countryCoefxList[0]?.id === Number(coefxID)
          ? "0"
          : null,
    },
    {
      col: "upper_threshold",
      mandatory: false,
      isMissingValue: null,
      type: VAR_TYPES.NUMBER,
      step: "0.01",
      editable: true,
      disabled: _.last(countryCoefxList)?.id === Number(coefxID) || !coefxID,
    },
    {
      col: "coefficient",
      mandatory: true,
      isMissingValue: null,
      type: VAR_TYPES.NUMBER,
      step: "0.01",
      editable: true,
    },
  ];
  const handleDelete = async (countryId) =>
    setDeleteData(countryList.find((e) => e.id === countryId));

  const validateDelete = async (countryId) => {
    try {
      await countryDeleteById(countryId);
    } catch (error) {
      console.error(error);
    }
  };

  const formatPayload = (payload) => {
    const defaultCur = payload.default_cur?.value || payload.default_cur;
    const currencies = payload?.currencies?.map((elm) => elm.value);
    return {
      ...payload,
      currencies,
      default_cur: currencies?.includes(defaultCur)
        ? defaultCur
        : currencies[0],
    };
  };

  const handleCreate = (payload) => {
    try {
      countryCreate(formatPayload(payload));
      navigateToParent(navigate, location);
    } catch (e) {
      console.error(e);
    }
  };
  const handleEdit = (payload) => {
    try {
      countryUpdateById(payload.id, formatPayload(payload));
      navigateToParent(navigate, location);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCoefxCreate = (payload) => {
    try {
      countryCoefxCreate({
        ...payload,
        bottom_threshold:
          countryCoefxList?.length === 0 ? 0 : payload?.bottom_threshold,
        country_id: Number(id),
      });
      navigate(-1);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCoefxEdit = (payload) => {
    try {
      countryCoefxUpdateById(payload.id, payload);
      navigate(-1);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCoefxDelete = async (countryCoefxId) => {
    setDeleteCoef(countryCoefxList.find((e) => e.id === countryCoefxId));
  };
  const validateCoefxDelete = async (countryCoefxId) => {
    try {
      await countryCoefxDeleteById(countryCoefxId);
    } catch (error) {
      console.error(error);
    }
  };

  const formatCountry = (country) => {
    const defautCurrencie = currencyList.find(
      (e) => e.id === country.default_cur
    );
    const currenciesSelected = currencyList.filter(
      (e) =>
        country?.currencies?.length > 0 && country.currencies.includes(e.id)
    );
    return {
      id: country?.id,
      name: country?.name,
      default_language: country?.default_language,
      mission_coefficient: country?.mission_coefficient,
      currencies:
        currenciesSelected.length > 0
          ? currenciesSelected.map((e) => e.currency_name).join(", ")
          : country.currencies,
      default_cur: defautCurrencie ? defautCurrencie.currency_name : null,
    };
  };
  const formatCountries = () => {
    return countryList.map((country) => {
      return formatCountry(country);
    });
  };

  return (
    <div>
      <div className="style-menu-row">
        <div className="style-item-menu">
          <Link to={`/${userScope}/settings/country-holidays`}>
            <FormattedMessage
              id={"admin_holidays_page"}
              defaultMessage={"Vacances par pays"}
            />
          </Link>
        </div>
      </div>
      <br />
      {action === ACTIONS.LIST && (
        <div className="flex">
          <Button
            className="btn-hover-color"
            size="large"
            color="blue"
            onClick={() => navigateView("new", navigate, location)}
          >
            <FormattedMessage
              id={"admin_create_new"}
              defaultMessage={"Nouveau"}
            />
          </Button>
        </div>
      )}
      {((action === ACTIONS.DETAILS && actionCoefx) || actionCoefx) && (
        <>
          <br />
          {(countryCoefxList?.length > 0 ||
            location.pathname.includes("new")) && (
            <TableManager
              action={actionCoefx}
              entity={ENTITIES.COUNTRY_COEF}
              entityPerId={
                countryCoefxList?.filter((e) => e.id == coefxID)[0] || {}
              }
              list={countryCoefxList}
              inputs={coeffInputs}
              navigateView={(propsId) =>
                navigateView(propsId, navigate, location, "coefx")
              }
              navigateEdit={(propsId) =>
                navigateEdit(propsId, navigate, location, "coefx")
              }
              handleCreate={handleCoefxCreate}
              handleDelete={handleCoefxDelete}
              handleEdit={(payload) => {
                handleCoefxEdit(payload);
              }}
            />
          )}
          {action === ACTIONS.DETAILS && (
            <div className="flex">
              <Button
                className="btn-hover-color"
                size="large"
                color="blue"
                onClick={() => navigate("coefx/new")}
              >
                <FormattedMessage
                  id={"admin_country_coef_page_create_new_coefficient_button"}
                  defaultMessage={"Nouveau coefficient"}
                />
              </Button>
            </div>
          )}
        </>
      )}
      {action && (
        <>
          <br />
          <TableManager
            action={action}
            entity={ENTITIES.COUNTRIES}
            entityPerId={{
              ...countryPerId,
              currencies: _.map(countryPerId?.currencies, (elm) => ({
                label: _.find(currencyList, { id: elm }).currency_name,
                value: elm,
              })),
            }}
            list={formatCountries()}
            formattedEntity={formatCountry(countryPerId)}
            inputs={inputs}
            navigateEdit={(propsId) =>
              navigateEdit(propsId, navigate, location)
            }
            navigateView={(propsId) =>
              navigateView(propsId, navigate, location)
            }
            handleCreate={handleCreate}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            watchFormInput={(payload) => {
              if (payload?.input === "currencies") {
                setDefaultCurrencyList(
                  payload?.value?.map((elm) => ({
                    name: elm.label,
                    value: elm.value,
                  }))
                );
              }
            }}
          />
        </>
      )}
      <HandleModaleBO
        setDeleteData={setDeleteData}
        validateDelete={validateDelete}
        deleteData={deleteData}
        entity={ENTITIES.COUNTRIES}
      />
      <HandleModaleBO
        setDeleteData={setDeleteCoef}
        validateDelete={validateCoefxDelete}
        deleteData={deleteCoef}
        entity={ENTITIES.COUNTRIES}
      />
    </div>
  );
}
